import {get, isEmpty, isNil} from 'lodash'
import {accountingTypes} from 'helpers/constants'
import {partCodes} from 'helpers/plans'
import {canEditInvoice, canRepostInvoice, isMarkedPosted, Posted_Invoice_Status} from 'helpers/invoices'
import {getXeroBillDeepLink, getXeroInvoiceDeepLinkUrl} from 'helpers/integrations/xero'
import {getQuickBooksBillDeepLinkUrl, getQuickBooksInvoiceDeepLinkUrl} from 'helpers/integrations/quickBooks'
import {canEditPurchaseInvoiceForPosting, canRepostPurchaseInvoice} from 'helpers/purchaseInvoices'

export const IntegrationStatus = {
  ALREADY_CONNECTED_TO_A_PRO_ACCOUNTING_PACKAGE: '-2',
  NOT_ALLOWED_TO_CONNECT: '-1',
  ALLOWED_TO_CONNECT: '0',
  ALREADY_CONNECTED: '1',
}

export const IntegrationSyncStatus = {
  OK: 1,
  RE_AUTH: 2,
  ERROR: 3,
}

export const documentProviders = {
  NONE: 'NONE',
  MOCK: 'MOCK',
  GOOGLE_DRIVE: 'GOOGLE_DRIVE',
  ONE_DRIVE: 'ONE_DRIVE',
  SHARE_POINT: 'SHAREPOINT',
}

export const getDocumentProviderName = (documentProvider) => {
  switch (documentProvider) {
    case documentProviders.ONE_DRIVE:
      return 'OneDrive'
    case documentProviders.SHARE_POINT:
      return 'SharePoint'
    case documentProviders.GOOGLE_DRIVE:
      return 'Google Drive'
    case documentProviders.MOCK:
      return 'Mock'
    case documentProviders.NONE:
      return 'None'
    default:
      return 'Invalid'
  }
}

export const isIntegratedWithDocuments = (documentProvider) => {
  return !isNil(documentProvider) && documentProvider !== documentProviders.NONE
}

export const isIntegratedWithApprovalDocProvider = (documentProvider) => {
  return !isNil(documentProvider) &&
    documentProvider !== documentProviders.GOOGLE_DRIVE &&
    documentProvider !== documentProviders.NONE
}

export const isIntegratingWithAnotherPackage = (accountsIntegration, integrationId) => {
  return (accountsIntegration && ![accountingTypes.NONE.id, integrationId]
    .includes(accountsIntegration.id))
}

export const getAccountingSystemName = (accountsIntegration) => {
  return isNil(accountsIntegration) || accountsIntegration === accountingTypes.NONE
    ? 'your accounts system'
    : accountsIntegration.title
}

export const getPostedInvoices = invoices => invoices.filter(invoice => invoice.posted)

export const getNonPostedInvoices = invoices => invoices.filter(invoice => !invoice.posted)

export const isAccountIntegrationEnabled = (accountsIntegration) => {
  const integrationId = get(accountsIntegration, 'id', accountingTypes.NONE.id)
  const proOnly = get(accountsIntegration, 'proOnly', false)
  return integrationId !== accountingTypes.NONE.id && !proOnly
}

export const isProOnlyAccountsIntegrationEnabled = (accountsIntegration) => {
  return get(accountsIntegration, 'proOnly', false)
}

export const isPostToAccountPackageDisabled = (accountsIntegration, invoices) => {
  if (isEmpty(invoices)) return !isAccountIntegrationEnabled(accountsIntegration)
  return !isAccountIntegrationEnabled(accountsIntegration)
    || isSomeInvoicesPending(invoices)
}

export const isSomeInvoicesPending = (invoices) => {
  return invoices.some(invoice => get(invoice, 'postStatus.value') === Posted_Invoice_Status.PENDING)
}

export const isPostToAccountPackageDisabledForInvoice = (user, accountsIntegration, invoice, isInvoice = true) => {
  const isEditable = isInvoice ? canEditInvoice(invoice, user) : canEditPurchaseInvoiceForPosting(user, invoice)
  const accountIntegrationEnabled = isAccountIntegrationEnabled(accountsIntegration)
  const isPosted = invoice.posted
  const postedStatus = get(invoice, 'postStatus.value', null)

  if (postedStatus === Posted_Invoice_Status.PENDING)
    return true

  if (isPosted)
    return true

  if (!isEditable)
    return true

  return !accountIntegrationEnabled
}

export const isRepostToAccountPackageDisabledForInvoice = (user, accountsIntegration, invoice, isInvoice = true) => {
  const postedStatus = get(invoice, 'postStatus.value', null)
  const isEditable = isInvoice
    ? canRepostInvoice(invoice, user)
    : canRepostPurchaseInvoice(user)
  const accountIntegrationEnabled = isAccountIntegrationEnabled(accountsIntegration)

  return postedStatus === Posted_Invoice_Status.PENDING || !isEditable
    ? true
    : !accountIntegrationEnabled
}

export const isMarkAsPostedEnabled = (invoice, user) => {
  const postedStatus = get(invoice, 'postStatus.value', null)
  return canEditInvoice(invoice, user) && !(postedStatus === Posted_Invoice_Status.PENDING)
}

export const isPurchaseInvoicePostEnabled = (invoice, readOnly) => {
  const postedStatus = get(invoice, 'postStatus.value', null)
  return !readOnly && !(postedStatus === Posted_Invoice_Status.PENDING)
}

export const isGoToAccountPackageEnabled = (invoice, accountsIntegration) => {
  if (isNil(invoice))
    return false

  return invoice.posted &&
    (accountsIntegration === accountingTypes.XERO || accountsIntegration === accountingTypes.QUICK_BOOKS_ONLINE) &&
    !isNil(invoice.accountingInvoiceId)
}

export const getInvoiceDeepLinkUrl = (accountsIntegration, xeroStatus, invoice, quickBooksBaseURL) => {
  switch (accountsIntegration.id) {
    case accountingTypes.XERO.id:
      return getXeroInvoiceDeepLinkUrl(xeroStatus.shortCode, invoice.creditNote, invoice.accountingInvoiceId)
    case accountingTypes.QUICK_BOOKS_ONLINE.id:
      return getQuickBooksInvoiceDeepLinkUrl(quickBooksBaseURL, invoice.creditNote, invoice.accountingInvoiceId)
    default:
      return null
  }
}

export const getBillDeepLink = (type, purchaseInvoice, quickBooksBaseURL) => {
  const isCreditNote = get(purchaseInvoice, 'creditNote', false)
  const accountingInvoiceId = get(purchaseInvoice, 'accountingInvoiceId', null)
  switch (type.id) {
    case accountingTypes.XERO.id:
      return getXeroBillDeepLink(isCreditNote, accountingInvoiceId)
    case accountingTypes.QUICK_BOOKS_ONLINE.id:
      return getQuickBooksBillDeepLinkUrl(quickBooksBaseURL, isCreditNote, accountingInvoiceId)
  }
}

export const isXeroIntegrationEnabled = (accountsIntegration) => {
  return get(accountsIntegration, 'id') === accountingTypes.XERO.id
    && isAccountIntegrationEnabled(accountsIntegration)
}

export const isSage50IntegrationEnabled = (accountsIntegration) => {
  return get(accountsIntegration, 'id') === accountingTypes.SAGE_50.id
    && isAccountIntegrationEnabled(accountsIntegration)
}

export const isOneDriveIntegrationAlreadyConnected = (oneDriveStatus) => {
  return get(oneDriveStatus, 'status.status', '0') === IntegrationStatus.ALREADY_CONNECTED
}

export const isSharePointIntegrationAlreadyConnected = (sharePointStatus) => {
  return get(sharePointStatus, 'status.status', '0') === IntegrationStatus.ALREADY_CONNECTED
}

export const isBaseSiteSet = (baseSite) => {
  return !isEmpty(baseSite)
}

export const isBaseDriveSet = (baseDrive) => {
  const driveId = get(baseDrive, 'id', null)
  return !isNil(baseDrive) && !isNil(driveId) && !isEmpty(driveId)
}

export const getFileFolders = (files) => {
  return !isNil(files)
    ? files.filter(file => get(file, 'isFolder', false))
    : []
}

export const getSubFolderArray = (selectedSubFolder, documentsBaseFolder) => {
  const baseFolder = {
    label: getBaseFolderNameFromPath(documentsBaseFolder),
    path: '',
  }
  if (isNil(selectedSubFolder)) {
    return [baseFolder]
  }
  const labels = selectedSubFolder.split('/').filter(label => label !== '')
  const subFolders = labels.map((label, index) => {
    const path = `/${labels.slice(0, index + 1).join('/')}`
    return {
      label,
      path,
    }
  })
  return [baseFolder, ...subFolders]
}

export const getBaseFolderNameFromPath = (path) => {
  if (isNil(path)) {
    return ''
  }
  const folders = path.split('/')
  return folders[folders.length - 1]
}

export const isUserAssignedSage50Package = (user) => {
  const userPackages = get(user, 'packages', [])
  return !isEmpty(userPackages) && userPackages.some(userPackage => userPackage.partCode === partCodes.sage50)
}

export const isPostToAccountsIntegrationVisible = (accountsIntegration, invoice, user) => {
  return !(
    get(invoice, 'posted')
    || !isAccountIntegrationEnabled(accountsIntegration)
    || (isSage50IntegrationEnabled(accountsIntegration) && !isUserAssignedSage50Package(user))
  )
}

export const isPostToAccountsIntegrationVisibleInPivotTable = (accountsIntegration, user) => {
  return !(
    !isAccountIntegrationEnabled(accountsIntegration)
    || (isSage50IntegrationEnabled(accountsIntegration) && !isUserAssignedSage50Package(user))
  )
}

export const areAllDocumentBasesSet = (baseSite, baseDrive, isBaseFolderSet) => {
  return isBaseSiteSet(baseSite) && isBaseDriveSet(baseDrive) && isBaseFolderSet
}

export const getPostToIntegrationAction = (integration, user, selectedRows, showPostToIntegration, onClick) => {
  if (!isPostToAccountsIntegrationVisibleInPivotTable(integration, user) || !showPostToIntegration) {
    return null
  }
  const markedAsPosted = selectedRows.filter(row => isMarkedPosted(row))
  const hasMarkedAsPosted = !isEmpty(markedAsPosted)

  return {
    className: 'postToAccountsIntegrationButton',
    selectAction: true,
    label: `Post To ${integration.title}`,
    disabled: hasMarkedAsPosted || isPostToAccountPackageDisabled(integration, selectedRows),
    onClick: onClick,
  }
}
